<template>
    <div v-if="activeDetailsTab === 'charts'">
        <div v-if="configsWithStoredDates" class="charts-wrapper">
            <div class="chart-grid" :style="{ '--column-number': multiView ? 1 : 2 }">
                <div
                    v-for="(config, index) in configsWithStoredDates"
                    :key="(config.info.id || index)"
                    class="chart module"
                >
                    <module-chart-with-options
                        :chart-config="config"
                        :title="config.info.name ?? ''"
                        :blocked-actions="NON_USER_DASHBOARD_BLOCKED_ACTIONS"
                        :date-override="props.dateOverride"
                        :collision-padding-top="props.collisionPaddingTop"
                        is-modal
                        display-options-top
                        @update:chart-date="updateChartDate($event, index)"
                    />
                </div>
            </div>
        </div>
    </div>
    <div v-else class="table-holder">
        <div class="tabbed-detail module" v-if="showSubTabTable">
            <generic-table
                v-if="tableData"
                :filename="plantBmuId"
                :data="tableData"
            />
            <div class="no-data" v-else>
                No data available
            </div>
        </div>
        <div class="metric-holder module" v-else-if="activeSubDetailsTab === 'Dynamic data'">
            <div class="metric" v-for="(item, key) in plantInfo.dynamicData" :key="key" v-tooltip="mapping[key].desc">
                <div class="title">{{ mapping[key].brief }}: </div>
                <div class="value">
                    {{ item ? item : "-" }}
                    <span>{{ mapping[key].units }}</span>
                </div>
            </div>
        </div>
        <div class="tabbed-detail module" v-else-if="currentExtraInfo">
            <div class="metric-holder" v-for="(itemList, key) in currentExtraInfo" :key="key">
                <div
                    class="metric"
                    v-for="(item, key) in itemList.innerLineData"
                    :key="`2-${key}`"
                >
                    <div class="title">{{ item.name }}: </div>
                    <div class="value">{{ item.value ? item.value : "-" }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ref, computed, nextTick, watch,
} from 'vue';
import { IModuleChartConfig } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { IPlantDetails, IPlantDetailsExtraData, IPlantDetailsExtraDataInner } from '@/interfaces/powerMarkets/plant/IPlantDetails.ts';
import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import { IDateRangeByNumberWithType } from '@/interfaces/general/IDateRangeByString.ts';
import { IDateRange } from '@/interfaces/general/IDateRange.ts';
import ModuleChartWithOptions from '@/components/powerMarkets/chart/ModuleChartWithOptions.vue';
import GenericTable from '@/components/powerMarkets/tables/GenericTable.vue';
import { getDatesFromConfig } from '@/helpers/datePickerMethod.ts';
import { chartApi } from '@/api/powerMarkets/chartApi.ts';
import { plantApi } from '@/api/powerMarkets/plantApi.ts';
import dayjs from 'dayjs';
import { IDashboardOverrides } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { NON_USER_DASHBOARD_BLOCKED_ACTIONS } from '@/interfaces/actions/enact/IModuleActions.ts';
import type { PlantDetailsTab } from '@/constants/powerMarkets/plantDetailsTabs.ts';
import { getPlantChartDate } from './plantChartDate.ts';

const props = defineProps<{
    plantBmuId: string;
    plantInfo: IPlantDetails;
    dateOverride?: IDashboardOverrides;
    chartDateRange?: IDateRange;
    activeDetailsTab?: PlantDetailsTab | '';
    activeSubDetailsTab?: string;
    charts: string[];
    multiView: boolean;
    collisionPaddingTop?: number;
}>();

const mapping: Record<string, { brief: string; desc: string; icon: string; units: string }> = {
    ndz: {
        brief: 'NDZ',
        desc: 'Notice to Deviate From Zero (minutes)',
        icon: '',
        units: 'mins',
    },
    ndb: {
        brief: 'NTB',
        desc: 'Notice to Deliver Bids (minutes)',
        icon: '',
        units: 'mins',
    },
    ndo: {
        brief: 'NTO',
        desc: 'Notice to Deliver Offers (minutes)',
        icon: '',
        units: 'mins',
    },
    mzt: {
        brief: 'MZT',
        desc: 'Minimum Zero Time (minutes)',
        icon: '',
        units: 'mins',
    },
    mnzt: {
        brief: 'MNZT',
        desc: 'Minimum Non Zero Time (minutes)',
        icon: '',
        units: 'mins',
    },
    sel: {
        brief: 'SEL',
        desc:
            'Stable Export Limit is a positive megawatt value, expressing the minimum stable operating level at which a particular BM Unit can export power to the transmission system.',
        icon: '',
        units: 'MW',
    },
    mdv: {
        brief: 'MDV',
        desc:
            'Maximum Delivery Volume, expressed in MWh, being the maximum number of MWhr of Offer (or Bid if MDV is negative) that a particular BM Unit may deliver within the associated Maximum Delivery Period.',
        icon: '',
        units: 'MW',
    },
    mdp: {
        brief: 'MDP',
        desc:
            'The Maximum Delivery Period is the time period over which the Maximum Delivery Volume can be delivered.',
        icon: '',
        units: 'mins',
    },
};

const remitData = ref<IGenericTableData | null>(null);
const configsToDisplay = ref<Array<IModuleChartConfig> | null>(null);
const configs = ref<Record<string, Array<IModuleChartConfig>> | null>(null);

const chartDateRanges = ref<Record<string, IDateObject>>({});
const ancillaryTabs = ref<HTMLElement | null>(null);

const showSubTabTable = computed<boolean>(() => props.activeDetailsTab === 'contracts' || props.activeDetailsTab === 'remit');
const tableData = computed<IGenericTableData | null>(() => (props.activeDetailsTab === 'contracts' ? selectedContractData.value : remitData.value));

const currentExtraInfo = computed<Array<IPlantDetailsExtraDataInner> | null>(() => {
    const extraData = props.plantInfo.extraData as Array<IPlantDetailsExtraData> | undefined;
    const matchedItem = extraData?.find(el => el.name === props.activeSubDetailsTab);
    const extraInfo = matchedItem?.innerData ?? null;
    return extraInfo;
});

const selectedContractData = computed<IGenericTableData | null>(() => {
    if (!props.plantInfo || !props.plantInfo.tableData) return null;
    if (props.activeSubDetailsTab === null) return props.plantInfo.tableData[0];

    return props.plantInfo.tableData !== null && props.plantInfo.tableData.length !== 0
        ? props.plantInfo.tableData.filter(
            (table: IGenericTableData) => table.tabName === props.activeSubDetailsTab,
        )[0]
        : null;
});

const configsWithStoredDates = computed<Array<IModuleChartConfig> | null>(() => {
    if (!configsToDisplay.value) return null;

    return configsToDisplay.value.map((config, index) => {
        const chartKey = config.info.id || `${config.info.name}-${index}`;
        const storedDate = chartDateRanges.value[chartKey];

        if (storedDate) {
            return {
                ...config,
                info: {
                    ...config.info,
                    date: mergeChartDate(config.info.date, storedDate),
                },
            };
        }

        return config;
    });
});

function updateChartDate (updatedDate: IDateObject, index: number): void {
    if (hasChartDateOverride(props.dateOverride?.chart)) return;
    if (!configsToDisplay.value) return;

    const newConfigs = [...configsToDisplay.value];
    const configToUpdate = { ...newConfigs[index] };

    configToUpdate.info = {
        ...configToUpdate.info,
        date: mergeChartDate(configToUpdate.info.date, updatedDate),
    };

    newConfigs[index] = configToUpdate;
    configsToDisplay.value = newConfigs;
}

function hasChartDateOverride (dateObject: IDateObject | null | undefined): dateObject is IDateObject {
    return !!(dateObject?.from || dateObject?.to);
}

function clearStoredChartDates (): void {
    chartDateRanges.value = {};
    configsToDisplay.value = null;
}

function mergeChartDate (
    currentDate: IModuleChartConfig['info']['date'],
    updatedDate: IDateObject,
): IModuleChartConfig['info']['date'] {
    if (!updatedDate.from || !updatedDate.to) return currentDate;

    return {
        ...currentDate,
        ...updatedDate,
        from: updatedDate.from,
        to: updatedDate.to,
    } as IModuleChartConfig['info']['date'];
}

function getChartDate (config: IModuleChartConfig): IDateRangeByNumberWithType {
    const override = props.dateOverride?.chart;
    if (hasChartDateOverride(override)) {
        const fromToDate = getDatesFromConfig(override);
        const from = (fromToDate.from || dayjs()).startOf('day').toDate();
        const to = (fromToDate.to || fromToDate.from || dayjs()).endOf('day').toDate();

        return {
            from: { type: 'date', value: from.getTime() },
            to: { type: 'date', value: to.getTime() },
        };
    }

    return getPlantChartDate(config, props.chartDateRange?.from, props.chartDateRange?.to);
}

async function getPlantRemit (): Promise<void> {
    const fromDate = dayjs()
        .subtract(1, 'day')
        .format('YYYY-MM-DD');
    const toDate = dayjs()
        .add(7, 'day')
        .format('YYYY-MM-DD');

    await plantApi.getPlantRemit(props.plantBmuId, fromDate, toDate).then((response) => {
        remitData.value = response.data;
    });
}

async function getPlantChartData (): Promise<void> {
    nextTick(async () => {
        configs.value = {};

        const plantId = props.plantBmuId;
        const chartPayload = props.charts.map((chart) => ({ plantId, chartType: chart }));
        if (chartPayload === undefined) return;

        await chartApi.getPlantChartSeriesConfig(chartPayload).then((response) => {
            const chartConfigs = response;

            const configsToAdd = chartConfigs.map((config) => ({
                ...config,
                info: {
                    ...config.info,
                    name: config.info.name ? config.info.name.split(' - ')[0] : '',
                    date: getChartDate(config),
                },
            }));

            configs.value = { ...configs.value, [plantId]: configsToAdd };

            configsToDisplay.value = configs.value[plantId];
        });
    });
}

function setTopOfAncillaryTabs (): void {
    const topOfAncillaryTabs = document.querySelector('div.p-tablist-tab-list') as HTMLElement;
    if (!topOfAncillaryTabs || !ancillaryTabs.value) return;

    ancillaryTabs.value.style.top = `${topOfAncillaryTabs.offsetHeight}px`;
}

async function initialise (): Promise<void> {
    await getPlantRemit();
    await getPlantChartData();

    nextTick(() => {
        setTopOfAncillaryTabs();
    });
}

watch(() => props.plantBmuId, async () => {
    await initialise();
});

watch(() => props.charts, async (_, oldValue) => {
    if (oldValue.length === 0) return;
    await getPlantChartData();
});

watch(() => props.dateOverride?.chart, async (newOverride, oldOverride) => {
    if (hasChartDateOverride(oldOverride) && !hasChartDateOverride(newOverride)) {
        clearStoredChartDates();
    }
    await getPlantChartData();
}, { deep: true });

initialise();

</script>

<style lang="scss" scoped>
.table-holder {
    .tabbed-detail {
        max-height: 600px;
        overflow: auto;
        .no-data {
            padding: var(--defaultSpacing);
        }
    }
}

.metric-holder {
    display: flex;
    flex-wrap: wrap;
    gap: var(--defaultSpacing);
    height: fit-content;
    align-content: stretch;
    margin: var(--defaultSpacing);
    .metric {
        background: var(--neutral400);
        border-radius: var(--standardBorderRadius);
        padding: var(--defaultSpacing);
        display: flex;
        justify-content: center;
        flex: 1 0 auto;
        gap: var(--defaultSpacing);
        min-width: fit-content;

        &.large-storetrack-metric {
            max-width: 50%;
        }

        .title {
            text-wrap: nowrap;
        }

        .value {
            font-weight: var(--boldFontWeight);
            text-wrap: nowrap;
        }

        .metric-block {
            background-color: inherit;
        }
    }
}

.module {
    border: solid 1px var(--neutral400);
    border-radius: var(--borderRadiusOuter);
}

.chart-grid {
    display: grid;
    grid-template-columns: repeat(var(--column-number), 1fr);
    gap: var(--largeSpacing);
    overflow: hidden;
    width: 100%;
    @include responsive('sm') {
        grid-template-columns: 1fr;
    }
    .chart {
        height: 40rem;
    }
}
</style>
